'use client'

import { CountUp } from '@/components/ui/count-up'

// Horizontal band of benchmark numbers on a dark navy card. Numbers
// count up on first viewport entry via CountUp (which already renders
// the final value immediately under prefers-reduced-motion, so this
// strip never animates for users who opted out).
//
// Content-integrity: every number below is either an already-vetted
// composite benchmark (same phrasing + sourcing as the homepage
// why-now band) or a plain Idukki product fact. Do not add stats here
// without a named public source or telemetry basis.

type Stat = {
  value: number
  prefix?: string
  suffix?: string
  label: string
  note: string
}

const STATS: Stat[] = [
  {
    value: 80,
    prefix: '~',
    suffix: '%',
    label: 'of consumers say UGC influences what they buy.',
    note: 'Composite of UGC-commerce benchmarks (Nosto, Bazaarvoice) + Idukki telemetry',
  },
  {
    value: 4,
    prefix: 'up to ',
    suffix: '×',
    label: 'Video reviews can convert better than text-only reviews on the same SKU.',
    note: 'Composite of UGC-commerce benchmarks (PowerReviews, Nosto/Stackla) + Idukki telemetry',
  },
  {
    value: 37,
    suffix: ' KB',
    label: 'Widget runtime, with no measurable hit to Core Web Vitals.',
    note: 'Idukki product fact, verified on live storefronts',
  },
  {
    value: 10,
    label: 'Social + review sources, every one included on every plan.',
    note: 'Instagram, TikTok, YouTube, X, LinkedIn, Threads + Google, Trustpilot, Feefo, TripAdvisor',
  },
]

export function UgcStatStrip({ className = '' }: { className?: string }) {
  return (
    <div className={className}>
      <dl className="grid gap-px overflow-hidden rounded-3xl bg-white/10 ring-1 ring-brand-navy/10 sm:grid-cols-2 lg:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.label} className="flex flex-col gap-3 bg-brand-navy p-7 md:p-8">
            <dd className="order-1 font-display text-4xl font-semibold tracking-tight text-brand-lime md:text-5xl">
              <CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} />
            </dd>
            <dt className="order-2 text-sm leading-snug text-white/85">{s.label}</dt>
            <dd className="order-3 mt-auto text-[11px] leading-snug text-white/45">{s.note}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
